import { useStore, useStoreMap } from 'effector-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { getStatusColor, roadAccidentModel } from '~/entities/RoadAccident'
import { usePermissions } from '~/entities/viewer'
import { editRoadAccidentModel } from '~/features/RoadAccidents/EditRoadAccident'
import {
  roadAccidentsEnumLabels,
  roadAccidentsEnumOptions,
  STATUS_VARIANT,
} from '~/shared/config/constants'
import { AutocompleteInput, FormProvider } from '~/shared/ui/Form'
import { statusChangeFx, StatusFormValues } from './model'

export function Status() {
  const { canRoadAccidentsUpdate } = usePermissions()
  const isChanged = useStore(statusChangeFx.pending)
  const isLoading = useStore(roadAccidentModel.requestFx.pending)

  const defaultValues = useStoreMap(
    editRoadAccidentModel.$roadAccident,
    (repairOrder) => {
      const status = repairOrder?.getStatus()
      return {
        status: status
          ? { id: status, label: roadAccidentsEnumLabels[status] }
          : null,
      }
    },
  )

  useEffect(() => {
    reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues])

  const form = useForm<StatusFormValues>({
    defaultValues,
  })
  const { reset, watch, setValue } = form

  const status = watch('status')?.id

  const statusColor = useMemo(() => getStatusColor(status), [status])

  const handleStatusChange = async (status: StatusFormValues['status']) => {
    try {
      setValue('status', status)
      await statusChangeFx(status)
    } catch (_) {
      reset(defaultValues)
    }
  }

  return (
    <FormProvider form={form}>
      <AutocompleteInput
        name='status'
        options={roadAccidentsEnumOptions}
        startDecorator={null}
        disableClearable
        skeletonShow={isLoading}
        readOnly={isChanged || !canRoadAccidentsUpdate}
        loading={isChanged}
        onChange={(_, value) =>
          handleStatusChange(value as StatusFormValues['status'])
        }
        size='sm'
        sx={{
          width: '190px',
          marginBottom: '8px',
          border: 'none',
          '&': {
            ...STATUS_VARIANT[statusColor],
          },

          '&.Mui-focused:before': {
            boxShadow: 'none',
          },
        }}
      />
    </FormProvider>
  )
}
